import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  getAgentCampaign,
  getAgentLeads,
  pauseAgentCampaign,
  resumeAgentCampaign,
  updateAgentCampaign,
  previewAgentQueries,
  resetAgentProgress,
} from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft,
  Play,
  Pause,
  Bot,
  Search,
  Send,
  Activity,
  Save,
  RotateCcw,
} from "lucide-react";

const StatusBadge = ({ status }) => {
  const map = {
    running: "badge-success",
    paused: "badge-warning",
    stopped: "badge-neutral",
  };
  return <span className={`badge ${map[status] || "badge-neutral"}`}>{status}</span>;
};

const LeadStatus = ({ status }) => {
  const map = {
    pending: "badge-neutral",
    emailed: "badge-success",
    failed: "badge-error",
    suppressed: "badge-warning",
  };
  return <span className={`badge ${map[status] || "badge-neutral"}`}>{status}</span>;
};

export default function AgentCampaignDetail() {
  const { id } = useParams();
  const [camp, setCamp] = useState(null);
  const [leads, setLeads] = useState([]);
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ prompt: "", rate_per_hour: 30, trial_link: "", custom_queries: "" });
  const [queryInfo, setQueryInfo] = useState(null);

  const load = async () => {
    try {
      const [c, l, q] = await Promise.all([
        getAgentCampaign(id),
        getAgentLeads(id),
        previewAgentQueries(id).catch(() => null),
      ]);
      setCamp(c);
      setLeads(l);
      setQueryInfo(q);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [id]);

  useEffect(() => {
    if (camp && !editing) {
      setForm({
        prompt: camp.prompt || "",
        rate_per_hour: camp.rate_per_hour || 30,
        trial_link: camp.trial_link || "https://botnira.com/",
        custom_queries: (camp.custom_queries || []).join("\n"),
      });
    }
  }, [camp, editing]);

  const togglePause = async () => {
    if (!camp) return;
    if (camp.status === "running") await pauseAgentCampaign(camp.id);
    else await resumeAgentCampaign(camp.id);
    load();
  };

  const saveSettings = async () => {
    try {
      const payload = {
        ...form,
        custom_queries: (form.custom_queries || "")
          .split("\n")
          .map((s) => s.trim())
          .filter(Boolean),
      };
      await updateAgentCampaign(id, payload);
      toast.success("Saved");
      setEditing(false);
      load();
    } catch {
      toast.error("Failed to save");
    }
  };

  if (!camp) return <div className="p-8 text-sm text-[#94A3B8]">Loading…</div>;

  return (
    <div className="p-8 max-w-[1400px]" data-testid="agent-detail-page">
      <Link
        to="/agent-campaigns"
        className="inline-flex items-center gap-1.5 text-sm text-[#475569] hover:text-[#002FA7] mb-4"
      >
        <ArrowLeft size={14} /> Back to AI Campaigns
      </Link>

      <div className="flex items-start justify-between mb-8">
        <div>
          <div className="section-label mb-2 flex items-center gap-2">
            <Bot size={12} /> AI Outbound Agent
          </div>
          <h1
            className="text-3xl font-semibold tracking-tight text-[#0F172A]"
            style={{ fontFamily: "Outfit" }}
            data-testid="agent-name"
          >
            {camp.name}
          </h1>
          <div className="flex items-center gap-3 mt-2 text-sm text-[#475569]">
            <StatusBadge status={camp.status} />
            <span>{camp.industry} · {camp.country}</span>
            <span>·</span>
            <span>{camp.rate_per_hour} emails/hour</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={async () => {
              try {
                await resetAgentProgress(id);
                toast.success("Search rotation reset — agent will scan new URLs");
                load();
              } catch {
                toast.error("Failed to reset");
              }
            }}
            className="btn-secondary inline-flex items-center gap-2"
            data-testid="btn-reset-progress"
            title="Reset the search rotation. Use if the agent seems stuck."
          >
            <RotateCcw size={13} /> Reset Search
          </button>
          <button
            onClick={togglePause}
            className="btn-primary inline-flex items-center gap-2"
            data-testid="btn-agent-toggle"
          >
            {camp.status === "running" ? (
              <><Pause size={14} /> Pause Agent</>
            ) : (
              <><Play size={14} /> Resume Agent</>
            )}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Score icon={Search} label="Leads Found" value={camp.leads_found} />
        <Score icon={Send} label="Emails Sent" value={camp.emails_sent} accent />
        <Score icon={Activity} label="Failed" value={camp.emails_failed} tone={camp.emails_failed > 0 ? "warn" : ""} />
        <Score icon={Bot} label="Rate" value={`${camp.rate_per_hour}/hr`} />
      </div>

      {camp.last_activity && (
        <div className="bg-white border border-[#E2E8F0] rounded-lg p-4 mb-6" data-testid="live-activity">
          <div className="section-label mb-1">Live activity</div>
          <div className="text-sm text-[#0F172A] font-mono">
            {camp.status === "running" && (
              <span className="inline-block w-2 h-2 rounded-full bg-[#10B981] animate-pulse mr-2" />
            )}
            {camp.last_activity}
          </div>
        </div>
      )}

      <div className="rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] p-4 mb-6" data-testid="delivery-check-tip">
        <div className="section-label mb-2">How to verify emails reached inboxes</div>
        <ul className="text-xs text-[#475569] space-y-1 leading-relaxed list-disc pl-5">
          <li>Open <Link to="/settings" className="text-[#002FA7] font-medium hover:underline">Settings</Link> → &ldquo;Check status&rdquo; to see delivered / bounced / opened webhook events from Resend.</li>
          <li>Or open your Resend dashboard → Logs — each Resend ID from the leads below can be looked up there.</li>
          <li>&ldquo;Emails Sent&rdquo; here = accepted by Resend API. &ldquo;Delivered&rdquo; only shows in Settings once the Resend webhook fires (typically within a minute).</li>
        </ul>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-[#E2E8F0] rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-[#E2E8F0]">
            <div className="section-label">Leads</div>
            <div className="text-lg font-semibold text-[#0F172A]" style={{ fontFamily: "Outfit" }}>
              {leads.length} discovered
            </div>
          </div>
          {leads.length === 0 ? (
            <div className="p-10 text-center text-sm text-[#94A3B8]" data-testid="empty-leads">
              The agent is searching. Leads will appear here as they&apos;re found.
            </div>
          ) : (
            <div className="max-h-[500px] overflow-y-auto">
              <table className="w-full">
                <thead className="sticky top-0 bg-[#F8FAFC]">
                  <tr className="border-b border-[#E2E8F0]">
                    <Th>Business</Th>
                    <Th>Email</Th>
                    <Th>Status</Th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((l) => (
                    <tr
                      key={l.id}
                      onClick={() => setSelected(l)}
                      className="border-b border-[#F1F5F9] hover:bg-slate-50 cursor-pointer"
                      data-testid={`lead-row-${l.id}`}
                    >
                      <td className="px-6 py-3 text-sm">
                        <div className="text-[#0F172A] font-medium">{l.company_name || "—"}</div>
                        <div className="text-xs text-[#94A3B8] truncate max-w-xs">{l.description || l.source_url}</div>
                      </td>
                      <td className="px-6 py-3 text-sm text-[#475569] font-mono text-xs">{l.email}</td>
                      <td className="px-6 py-3"><LeadStatus status={l.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Right side: settings & selected lead preview */}
        <div className="space-y-6">
          <div className="bg-white border border-[#E2E8F0] rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="section-label">Settings</div>
              {editing ? (
                <button
                  onClick={saveSettings}
                  className="btn-primary inline-flex items-center gap-1.5 text-xs px-3 py-1.5"
                  data-testid="btn-save-agent-settings"
                >
                  <Save size={12} /> Save
                </button>
              ) : (
                <button
                  onClick={() => setEditing(true)}
                  className="btn-secondary text-xs px-3 py-1.5"
                  data-testid="btn-edit-agent-settings"
                >
                  Edit
                </button>
              )}
            </div>

            <label className="block text-xs font-medium mb-1.5">Email prompt</label>
            <textarea
              className="field-input font-mono text-xs"
              rows={4}
              value={form.prompt}
              disabled={!editing}
              onChange={(e) => setForm({ ...form, prompt: e.target.value })}
              data-testid="textarea-agent-detail-prompt"
            />

            <label className="block text-xs font-medium mt-3 mb-1.5">Trial link</label>
            <input
              className="field-input font-mono text-xs"
              value={form.trial_link}
              disabled={!editing}
              onChange={(e) => setForm({ ...form, trial_link: e.target.value })}
            />

            <label className="block text-xs font-medium mt-3 mb-1.5">Rate (emails/hour)</label>
            <input
              type="number"
              min="1"
              max="60"
              className="field-input"
              value={form.rate_per_hour}
              disabled={!editing}
              onChange={(e) => setForm({ ...form, rate_per_hour: Number(e.target.value) })}
            />

            <div className="flex items-center justify-between mt-4 mb-1.5">
              <label className="block text-xs font-medium">
                Search keywords{" "}
                <span className="text-[10px] text-[#94A3B8] font-normal">
                  {queryInfo ? `(${queryInfo.source})` : ""}
                </span>
              </label>
            </div>
            <textarea
              className="field-input font-mono text-xs"
              rows={5}
              value={form.custom_queries}
              disabled={!editing}
              onChange={(e) => setForm({ ...form, custom_queries: e.target.value })}
              placeholder="One search query per line. Leave empty to auto-generate from country + industry."
              data-testid="textarea-agent-detail-queries"
            />
            <p className="text-[10px] text-[#94A3B8] mt-1.5 leading-relaxed">
              {editing
                ? "One per line. Save to update. Empty = auto-generate from country + industry."
                : queryInfo?.source === "custom"
                ? `Using ${queryInfo.queries.length} custom queries.`
                : `Using auto-generated queries (${queryInfo?.queries?.length || 0}).`}
            </p>

            {!editing && queryInfo && queryInfo.queries && queryInfo.queries.length > 0 && (
              <details className="mt-2" data-testid="active-queries-preview">
                <summary className="text-[11px] text-[#002FA7] cursor-pointer hover:underline">
                  Show active queries
                </summary>
                <ul className="mt-2 space-y-1 text-[11px] text-[#475569] font-mono max-h-40 overflow-y-auto">
                  {queryInfo.queries.map((q, i) => (
                    <li key={i} className="truncate">• {q}</li>
                  ))}
                </ul>
              </details>
            )}
          </div>

          {selected && (
            <div className="bg-white border border-[#E2E8F0] rounded-lg p-6" data-testid="lead-preview">
              <div className="section-label mb-3">Lead preview</div>
              <div className="text-sm font-semibold text-[#0F172A]">{selected.company_name || "(no company)"}</div>
              <div className="text-xs text-[#94A3B8] mt-0.5">{selected.email}</div>
              {selected.source_url && (
                <a
                  href={selected.source_url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-[#002FA7] hover:underline block mt-1 truncate"
                >
                  {selected.source_url}
                </a>
              )}
              {selected.subject && (
                <>
                  <div className="text-xs font-semibold uppercase text-[#64748B] mt-4">Subject</div>
                  <div className="text-sm text-[#0F172A]">{selected.subject}</div>
                  <div className="text-xs font-semibold uppercase text-[#64748B] mt-3">Body</div>
                  <pre className="text-xs text-[#475569] whitespace-pre-wrap font-sans mt-1 leading-relaxed">
                    {selected.body}
                  </pre>
                </>
              )}
              {selected.error && (
                <div className="mt-3 text-xs text-[#B91C1C] bg-[#FFF1F2] rounded p-2">
                  {selected.error}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const Score = ({ icon: Icon, label, value, accent, tone }) => (
  <div className={`scorecard ${tone === "warn" ? "!bg-[#FEF3C7]" : ""}`}>
    <div className="flex items-center justify-between">
      <div className="section-label">{label}</div>
      <div className={`w-8 h-8 rounded-md flex items-center justify-center ${accent ? "bg-[#E5E9F6] text-[#002FA7]" : "bg-[#F1F5F9] text-[#475569]"}`}>
        <Icon size={14} />
      </div>
    </div>
    <div className="text-3xl font-semibold tracking-tight text-[#0F172A] tabular-nums" style={{ fontFamily: "Outfit" }}>
      {value}
    </div>
  </div>
);

const Th = ({ children }) => (
  <th className="px-6 py-3 text-xs font-semibold uppercase tracking-wider text-[#64748B] text-left">
    {children}
  </th>
);
